export const STANDARD_SHEET_TAB_NAMES = Object.freeze({
    scheda: 'scheda',
    collegamenti: 'collegamenti',
    geolocalizzazione: 'geolocalizzazione',
});

export const POMPEI_SHEET_TAB_NAMES = Object.freeze({
    rtMisure: 'pompei_rt_misure',
    ucMonitoraggio: 'pompei_uc_monitoraggio',
});

export const POMPEI_REFERENTE_TOPOGRAFICO_SHEET = Object.freeze({
    name: 'referente_topografico',
    model: 'App\\Models\\PompeiReferenteTopografico',
});

// Pompei topographic sheets that can host the rectified-image measurements tab
// (`pompei_rt_misure`): the Referente Topografico itself plus the hierarchy
// "node" sheets (Regio → Struttura). They all link to a topographic referent and
// carry rectified graphic documentation, so the measurements viewer applies to
// any of them. Mirrors the backend PompeiHierarchySheetRegistry sheets that use
// the HasPompeiRtDocumentazioneGrafica trait.
export const POMPEI_TOPOGRAPHIC_SHEET_NAMES = Object.freeze([
    POMPEI_REFERENTE_TOPOGRAFICO_SHEET.name,
    'pompei_regio',
    'pompei_insula',
    'pompei_strada',
    'pompei_marciapiede',
    'pompei_ambiente',
    'pompei_parete',
    'pompei_pavimento',
    'pompei_arredo',
    'pompei_struttura',
]);

export const isPompeiTopographicSheet = (sheetName) => POMPEI_TOPOGRAPHIC_SHEET_NAMES.includes(sheetName);

const DEFAULT_TAB_PRESENTATION = Object.freeze({
    [STANDARD_SHEET_TAB_NAMES.scheda]: {
        title: 'Scheda',
        icon: 'tab-sheet',
        variantTitle: 'Scheda',
    },
    [STANDARD_SHEET_TAB_NAMES.collegamenti]: {
        title: 'COLLEGAMENTI',
        icon: 'tab-collegamenti',
        variantTitle: 'Collegamenti',
    },
    [STANDARD_SHEET_TAB_NAMES.geolocalizzazione]: {
        title: 'Geolocalizzazione',
        icon: 'tab-geopin',
        variantTitle: 'Geolocalizzazione',
    },
    [POMPEI_SHEET_TAB_NAMES.rtMisure]: {
        title: 'Misura',
        icon: 'tab-misura',
        variantTitle: 'Misura',
    },
    [POMPEI_SHEET_TAB_NAMES.ucMonitoraggio]: {
        title: 'MONITORAGGIO',
        icon: 'tab-monitoraggio',
        variantTitle: 'Monitoraggio',
    },
});

const THEME_TAB_PRESENTATION = Object.freeze({
    Carracci: {
        [STANDARD_SHEET_TAB_NAMES.collegamenti]: {
            title: 'CONTENUTO',
        },
        [STANDARD_SHEET_TAB_NAMES.geolocalizzazione]: {
            title: 'Geotag',
            variantTitle: 'Geotag',
        },
    },
    Pompei: {
        [STANDARD_SHEET_TAB_NAMES.scheda]: {
            title: (context) => isPompeiReferenteTopograficoWithVariants(context) ? 'Ref. top.' : 'Scheda',
        },
        [STANDARD_SHEET_TAB_NAMES.geolocalizzazione]: {
            title: 'CARTOGRAFIA',
            variantTitle: 'Cartografia',
        },
    },
});

const resolveValue = (value, context) => typeof value === 'function' ? value(context) : value;

const mergePresentation = (base, override = {}) => ({
    ...base,
    ...override,
});

const normalizeModelClass = (modelClass) => String(modelClass ?? '').replace(/\\{2,}/g, '\\');

export const isPompeiReferenteTopograficoSheet = (sheet) => {
    const modelClass = normalizeModelClass(sheet?.laravel_model);

    return sheet?.name === POMPEI_REFERENTE_TOPOGRAFICO_SHEET.name
        || modelClass === POMPEI_REFERENTE_TOPOGRAFICO_SHEET.model
        || modelClass.endsWith('\\PompeiReferenteTopografico');
};

export const hasSheetVariants = (record) => {
    const variants = record?.polymorphic_variants;
    return Array.isArray(variants) && variants.some((variant) => Boolean(variant?.tab_name));
};

export const isPompeiReferenteTopograficoWithVariants = ({ theme, sheet, record } = {}) => {
    return theme === 'Pompei'
        && isPompeiReferenteTopograficoSheet(sheet)
        && hasSheetVariants(record);
};

export const resolveSheetTabPresentation = ({ tabName, theme, sheet = null, record = null, translate = null, locale = 'it' } = {}) => {
    const base = DEFAULT_TAB_PRESENTATION[tabName] ?? {
        title: tabName,
        icon: 'tab-sheet',
        variantTitle: tabName,
    };
    const override = THEME_TAB_PRESENTATION[theme]?.[tabName];
    const merged = mergePresentation(base, override);
    const context = { tabName, theme, sheet, record };

    let title = resolveValue(merged.title, context) ?? tabName;
    let variantTitle = resolveValue(merged.variantTitle, context) ?? title;
    if (theme === 'Pompei' && typeof translate === 'function') {
        const label = (key, fallback) => translate(key, fallback);
        const upper = value => value.toLocaleUpperCase(locale);
        if (tabName === STANDARD_SHEET_TAB_NAMES.scheda) {
            variantTitle = label('core.recordForm.tabs.record', variantTitle);
            title = isPompeiReferenteTopograficoWithVariants(context)
                ? label('pompei.sheetTabs.topographicReferent', title)
                : variantTitle;
        } else if (tabName === STANDARD_SHEET_TAB_NAMES.collegamenti) {
            variantTitle = label('core.recordForm.tabs.links', variantTitle);
            title = upper(variantTitle);
        } else if (tabName === STANDARD_SHEET_TAB_NAMES.geolocalizzazione) {
            variantTitle = label('core.recordForm.tabs.map', variantTitle);
            title = upper(variantTitle);
        } else if (tabName === POMPEI_SHEET_TAB_NAMES.rtMisure) {
            title = variantTitle = label('pompei.sheetTabs.measurement', title);
        } else if (tabName === POMPEI_SHEET_TAB_NAMES.ucMonitoraggio) {
            variantTitle = label('pompei.sheetTabs.monitoring', variantTitle);
            title = upper(variantTitle);
        }
    }

    return {
        title,
        icon: resolveValue(merged.icon, context) ?? 'tab-sheet',
        variantTitle,
    };
};

export const variantEntityLabel = (variant) => String(variant?.label ?? '')
    .replace(/^scheda\s+/i, '')
    .trim();

export const isPompeiLeadingVariantTab = ({ theme, sheet, record, variant, tabName } = {}) => {
    const variantSheetName = variant?.sheet?.name ?? String(variant?.tab_name ?? '').replace(/^variant_/, '');
    return isPompeiReferenteTopograficoWithVariants({ theme, sheet, record })
        && tabName === STANDARD_SHEET_TAB_NAMES.scheda
        && (variantSheetName === 'pompei_insula'
            || /^scheda\s+insula$/i.test(String(variant?.label ?? '').trim()));
};

export const resolveVariantTabTitle = ({ variant, tabName, theme, sheet = null, record = null, translate = null, locale = 'it' } = {}) => {
    const presentation = resolveSheetTabPresentation({ tabName, theme, sheet, record, translate, locale });
    const variantSheetName = variant?.sheet?.name ?? String(variant?.tab_name ?? '').replace(/^variant_/, '');
    if (theme === 'Pompei' && typeof translate === 'function' && variantSheetName) {
        const fallback = variantEntityLabel(variant);
        const entity = translate(`pompei.sheetTabs.entities.${variantSheetName}`, fallback);
        if (isPompeiLeadingVariantTab({ theme, sheet, record, variant, tabName })) {
            return presentation.variantTitle;
        }
        if (entity) return `${presentation.variantTitle} · ${entity}`;
    }
    const entity = variantEntityLabel(variant);

    if (tabName === STANDARD_SHEET_TAB_NAMES.scheda) {
        if (isPompeiLeadingVariantTab({ theme, sheet, record, variant, tabName })) {
            return 'Scheda';
        }

        return String(variant?.label ?? '').trim() || presentation.variantTitle;
    }

    return entity
        ? `${presentation.variantTitle} ${entity}`
        : presentation.title;
};
